import { useEffect } from "react"
import styled, { keyframes } from "styled-components"

const ResultsModal = ({ status, onClose }) => {
    const isSuccess = status === "success"

    // Close on Escape
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose()
        }
        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [onClose])

    return (
        <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
            <ModalCard role="dialog" aria-modal="true" aria-live="polite" $variant={status}>
                <IconBadge $variant={status}>
                    {isSuccess ? (
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </IconBadge>

                <ModalTitle>
                    {isSuccess ? "Request received" : "Submission didn't go through"}
                </ModalTitle>

                <ModalMessage>
                    {isSuccess
                        ? "Thanks for reaching out. We'll review your project details and get back to you as soon as possible."
                        : "Something went wrong on our end. Please wait a moment and try submitting the form again."}
                </ModalMessage>

                <ModalButton type="button" onClick={onClose} $variant={status}>
                    {isSuccess ? "Done" : "Close"}
                </ModalButton>
            </ModalCard>
        </ModalOverlay>
    )
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

const riseIn = keyframes`
    from { opacity: 0; transform: translateY(12px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
`

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(8, 32, 74, 0.55);
    backdrop-filter: blur(3px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 1000;
    animation: ${fadeIn} 0.18s ease-out;
`

const ModalCard = styled.div`
    width: 100%;
    max-width: 400px;
    background-color: #0D47A1;
    color: #E3F2FD;
    border-radius: 16px;
    border: 1px solid ${(props) => (props.$variant === "success" ? "rgba(165, 214, 167, 0.4)" : "rgba(255, 138, 128, 0.4)")};
    box-shadow: 0 24px 48px rgba(8, 32, 74, 0.45);
    padding: 32px 28px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    animation: ${riseIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1);

    @media (max-width: 420px) {
        padding: 24px 18px 20px;
    }
`

const IconBadge = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
    background-color: ${(props) => (props.$variant === "success" ? "rgba(165, 214, 167, 0.15)" : "rgba(255, 138, 128, 0.15)")};
    color: ${(props) => (props.$variant === "success" ? "#A5D6A7" : "#FF8A80")};
`

const ModalTitle = styled.p`
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.2px;
    margin: 0 0 10px;
`

const ModalMessage = styled.p`
    font-size: 14px;
    line-height: 1.5;
    color: rgba(227, 242, 253, 0.85);
    margin: 0 0 24px;
`

const ModalButton = styled.button`
    width: 100%;
    height: 44px;
    border-radius: 8px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.1s ease;
    background-color: ${(props) => (props.$variant === "success" ? "#2196F3" : "transparent")};
    color: ${(props) => (props.$variant === "success" ? "#E3F2FD" : "#90CAF9")};
    box-shadow: ${(props) => (props.$variant === "success" ? "none" : "inset 0 0 0 1px #90CAF9")};

    &:hover {
        background-color: ${(props) => (props.$variant === "success" ? "#0D47A1" : "rgba(144, 202, 249, 0.12)")};
        box-shadow: inset 0 0 0 1px #90CAF9;
    }
    &:active {
        transform: scale(0.97);
    }
`

export default ResultsModal